import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as rpc from "../../../common/jsonRpcRoutes";

interface PreorderExpiryWarningProps {
  readonly bookingId?: string;
  readonly active: boolean;
  readonly onExpired: () => void | Promise<void>;
  /** Bumps the server TTL resync whenever a successful basket-changing request completes. */
  readonly resyncKey?: number;
  readonly expireWhenInactive?: boolean;
}

const INACTIVE_CACHE_POLL_INTERVAL_MS = 5000;

export function PreorderExpiryWarning({
  bookingId,
  active,
  onExpired,
  resyncKey,
  expireWhenInactive = false,
}: PreorderExpiryWarningProps) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [expiryTime, setExpiryTime] = useState<number | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const synchronizationGenerationRef = useRef(0);
  const refreshedExpiryRef = useRef<number | null>(null);
  const lastResyncKeyRef = useRef(resyncKey);
  const environment = import.meta.env.VITE_ENVIRONMENT ?? "development";
  const logCountdown = environment === "development" || environment === "qa";

  const clearWarningTimer = useCallback(() => {
    if (warningTimerRef.current !== null) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, []);

  const synchronize = useCallback(async () => {
    const synchronizationGeneration = ++synchronizationGenerationRef.current;
    clearWarningTimer();
    if (!active || !bookingId) {
      setShow(false);
      setExpiryTime(null);
      return;
    }

    try {
      const status = await rpc.call("/getPreorderCacheStatus", { booking_id: bookingId });
      if (synchronizationGeneration !== synchronizationGenerationRef.current) return;
      if (!status.active || status.remainingSeconds === null) {
        setShow(false);
        setExpiryTime(null);
        if (expireWhenInactive) {
          await onExpired();
          return;
        }
        warningTimerRef.current = setTimeout(() => {
          void synchronize();
        }, INACTIVE_CACHE_POLL_INTERVAL_MS);
        return;
      }

      const synchronizedExpiryTime = Date.now() + status.remainingSeconds * 1000;
      setExpiryTime(synchronizedExpiryTime);
      const secondsUntilWarning = status.remainingSeconds - status.warningRemainingSeconds;
      if (secondsUntilWarning <= 0) {
        setShow(true);
        return;
      }

      setShow(false);
      warningTimerRef.current = setTimeout(() => {
        void synchronize();
      }, secondsUntilWarning * 1000);
    } catch (error) {
      if (synchronizationGeneration === synchronizationGenerationRef.current) {
        console.error("Failed to synchronize pre-order expiry warning:", error);
      }
    }
  }, [active, bookingId, clearWarningTimer, expireWhenInactive, onExpired]);

  useEffect(() => {
    void synchronize();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") void synchronize();
    };
    window.addEventListener("focus", synchronize);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      synchronizationGenerationRef.current += 1;
      clearWarningTimer();
      window.removeEventListener("focus", synchronize);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [clearWarningTimer, synchronize]);

  useEffect(() => {
    if (resyncKey === undefined) return;
    if (resyncKey === lastResyncKeyRef.current) return;
    lastResyncKeyRef.current = resyncKey;
    void synchronize();
  }, [resyncKey, synchronize]);

  useEffect(() => {
    if (expiryTime === null) return;

    const updateCountdown = (): boolean => {
      const nextRemainingSeconds = Math.max(0, Math.ceil((expiryTime - Date.now()) / 1000));
      if (logCountdown) {
        const countdown = `${Math.floor(nextRemainingSeconds / 60)
          .toString()
          .padStart(2, "0")}:${(nextRemainingSeconds % 60).toString().padStart(2, "0")}`;
        console.info(`[preorder-cache] Expires in ${countdown}`);
      }
      if (nextRemainingSeconds > 0) return false;
      if (refreshedExpiryRef.current !== expiryTime) {
        refreshedExpiryRef.current = expiryTime;
        setShow(false);
        onExpired();
      }
      return true;
    };
    if (updateCountdown()) return;
    const interval = window.setInterval(() => {
      if (updateCountdown()) window.clearInterval(interval);
    }, 1000);
    return () => window.clearInterval(interval);
  }, [expiryTime, logCountdown, onExpired]);

  if (!show) return null;

  return (
    <p className="preorder-expiry-warning widget-warning-panel" aria-live="polite">
      {t(
        "preorder_expiry_warning_message",
        "We’ll save your selected pre-orders for a further 2 hours. Please complete your selection soon to avoid re-ordering.",
      )}
    </p>
  );
}
